import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ConfigProvider, Layout, theme, Typography, message } from 'antd';
import { MedicineBoxOutlined, ShoppingCartOutlined, InboxOutlined, BarChartOutlined, UserOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { store } from './store';
import { useInventoryHighlighting } from './hooks/useInventoryHighlighting';

// Components
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import NotificationSystem from './components/NotificationSystem';
import ProtectedRoute from './components/ProtectedRoute';
import Dashboard from './pages/Dashboard';
import Branches from './pages/Branches';
import Medicines from './pages/Medicines';
import Sales from './pages/Sales';
import Login from './pages/Login';
import Registration from './pages/Registration';
import InventoryRequests from './pages/InventoryRequests';
import BranchRequestOrder from './components/BranchRequestOrder';
import Profile from './pages/Profile';
import BranchProfile from './components/BranchProfile';
import BranchRequests from './components/BranchRequests';
import BranchNavbar from './components/BranchNavbar';
import BranchSidebar from './components/BranchSidebar';
import MedicineManagement from './components/MedicineManagement';
import CheckoutForm from './components/CheckoutForm';

const { Content } = Layout;

function App() {
  return (
    <Provider store={store}>
      <ConfigProvider
        theme={{
          algorithm: theme.defaultAlgorithm,
          token: {
            colorPrimary: '#1890ff',
            colorSuccess: '#52c41a',
            colorWarning: '#faad14',
            colorError: '#ff4d4f',
            colorInfo: '#1890ff',
          },
        }}
      >
        <Router>
          <AppContent />
        </Router>
      </ConfigProvider>
    </Provider>
  );
}

function AppContent() {
  const [sidebarOpen, setSidebarOpen] = React.useState(true);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <Routes>
      {/* Default redirect to login */}
      <Route path="/" element={<Login />} />
      
      {/* Login page - no layout */}
      <Route path="/login" element={<Login />} />
      
      {/* Registration page - no layout */}
      <Route path="/register" element={<Registration />} />
      
      {/* Redirect /dashboard to /main/ for backward compatibility */}
      <Route path="/dashboard" element={<Navigate to="/main/" replace />} />
      
      {/* Main Seller Dashboard - with layout */}
      <Route path="/main/*" element={
        <ProtectedRoute requireAuth={true}>
          <MainSellerLayout 
            sidebarOpen={sidebarOpen} 
            onSidebarToggle={handleSidebarToggle}
          />
        </ProtectedRoute>
      } />

      {/* Branch Manager Dashboard - with layout */}
      <Route path="/branch/*" element={
        <ProtectedRoute requireAuth={true}>
          <BranchLayout />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function MainSellerLayout({ sidebarOpen, onSidebarToggle }: { sidebarOpen: boolean; onSidebarToggle: () => void }) {
  const { hasPendingInventoryRequests, hasLowStockAlerts } = useInventoryHighlighting();
  
  // Get user data from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  // Debug logging for user data
  console.log('MainSellerLayout - User data:', user);
  console.log('MainSellerLayout - User branch:', user?.branch);
  console.log('MainSellerLayout - User branch_id:', user?.branch_id); // fallback
  console.log('MainSellerLayout - User is_main_seller:', user?.is_main_seller);
  console.log('MainSellerLayout - User is_branch_manager:', user?.is_branch_manager);
  
  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Navbar onMenuClick={onSidebarToggle} />
      <Layout>
        <Sidebar 
          open={sidebarOpen} 
          onClose={() => onSidebarToggle()} 
          hasPendingInventoryRequests={hasPendingInventoryRequests}
          hasLowStockAlerts={hasLowStockAlerts}
        />
        <Layout style={{ marginLeft: sidebarOpen ? 200 : 0, transition: 'margin 0.3s ease' }}>
          <Content style={{ padding: '24px', marginTop: '64px' }}>
            <Routes>
              <Route index element={<Dashboard />} />
              <Route path="branches" element={<Branches />} />
              <Route path="medicines" element={<Medicines />} />
              <Route path="inventory-requests" element={<InventoryRequests branchId={user?.branch || user?.branch_id} />} />
              <Route path="profile" element={<Profile />} />
              {/* <Route path="sales" element={<Sales />} /> */}
              {/* <Route path="analytics" element={<Analytics />} /> */}
            </Routes>
          </Content>
        </Layout>
      </Layout>
      <NotificationSystem />
    </Layout>
  );
}

function BranchLayout() {
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const [selectedKey, setSelectedKey] = React.useState('medicines');
  const [user, setUser] = React.useState<any>(null);
  const [cartItems, setCartItems] = useState<any[]>([]);
  const navigate = useNavigate();
  const location = useLocation();

  // Load user data from localStorage
  React.useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        console.log('Branch Layout - User data from localStorage:', parsedUser);
        console.log('Passing to BranchProfile - branchId:', parsedUser?.branch || parsedUser?.branch_id, 'managerId:', parsedUser?.id);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  // Cart functions
  const handleAddToCart = (medicine: any, quantity: number) => {
    console.log('Adding to cart:', { medicine, quantity });
    
    // Check if medicine has sufficient stock
    if (medicine.quantity_available !== undefined && medicine.quantity_available < quantity) {
      message.error(`Insufficient stock. Only ${medicine.quantity_available} units available.`);
      return;
    }

    const existingItem = cartItems.find(item => item.medicine.branch_medicine_id === medicine.branch_medicine_id);
    const numericPrice = typeof medicine.unit_price === 'string' ? parseFloat(medicine.unit_price) : medicine.unit_price;
    
    if (existingItem) {
      const updatedItems = cartItems.map(item => {
        if (item.medicine.branch_medicine_id === medicine.branch_medicine_id) {
          const newQuantity = item.quantity + quantity;
          const hikeMultiplier = (medicine.hike_percent && medicine.hike_percent > 0) ? (1 + medicine.hike_percent / 100) : 1;
          const finalPrice = numericPrice * hikeMultiplier;
          return {
            ...item,
            quantity: newQuantity,
            totalPrice: finalPrice * newQuantity
          };
        }
        return item;
      });
      setCartItems(updatedItems);
    } else {
      const hikeMultiplier = (medicine.hike_percent && medicine.hike_percent > 0) ? (1 + medicine.hike_percent / 100) : 1;
      const finalPrice = numericPrice * hikeMultiplier;
      const newItem = {
        medicine: {
          branch_medicine_id: medicine.branch_medicine_id,
          medicine_name: medicine.medicine_name,
          manufacturer: medicine.manufacturer,
          unit_price: numericPrice,
          hike_percent: medicine.hike_percent,
          quantity_available: medicine.quantity_available
        },
        quantity,
        unitPrice: finalPrice,
        totalPrice: finalPrice * quantity
      };
      setCartItems([...cartItems, newItem]);
    }
    
    message.success(`${medicine.medicine_name} added to cart`);
  };

  const handleUpdateCart = (updatedItems: any[]) => {
    setCartItems(updatedItems);
  };

  const handleClearCart = () => {
    setCartItems([]);
    message.success('Cart cleared');
  };

  const handleCheckoutSuccess = () => {
    setCartItems([]);
    message.success('Checkout completed successfully');
    // Note: Sales data will be automatically updated via Redux store
    // when the createSale action is dispatched from CheckoutForm
  };

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleTabChange = (key: string) => {
    setSelectedKey(key);
    navigate(`/branch/${key}`);
  };

  // Update selected key based on current route
  React.useEffect(() => {
    const currentPath = location.pathname;
    const urlSelectedKey = currentPath.split('/').pop() || 'medicines';
    setSelectedKey(urlSelectedKey);
  }, [location.pathname]);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <BranchNavbar 
        onMenuClick={handleSidebarToggle}
        branchName="Branch Medicine Management"
      />
      
      <BranchSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(true)}
        onTabChange={handleTabChange}
        activeTab={selectedKey}
      />
      
      <Layout style={{ marginLeft: sidebarOpen ? 200 : 0, transition: 'margin-left 0.3s ease' }}>
        <Content style={{ 
          margin: '24px',
          padding: '24px',
          background: '#f5f5f5',
          borderRadius: '8px',
          minHeight: 'calc(100vh - 112px)',
          marginTop: '88px' // Account for fixed header
        }}>
          <Routes>
            <Route index element={<Navigate to="/branch/medicines" replace />} />
            <Route path="medicines" element={
              <div>
                <div style={{ marginBottom: '24px' }}>
                  <Typography.Title level={2}>
                    <MedicineBoxOutlined /> Branch Medicine Management
                  </Typography.Title>
                </div>
                <MedicineManagement 
                  branchId={user?.branch || user?.branch_id}
                  showAddToCart={true}
                  onAddToCart={handleAddToCart}
                />
              </div>
            } />
            <Route path="checkout" element={
              <div>
                <div style={{ marginBottom: '24px' }}>
                  <Typography.Title level={2}>
                    <ShoppingCartOutlined /> Checkout & Sales
                  </Typography.Title>
                </div>
                <CheckoutForm
                  cartItems={cartItems}
                  onUpdateCart={handleUpdateCart}
                  onClearCart={handleClearCart}
                  onCheckoutSuccess={handleCheckoutSuccess}
                  branchId={user?.branch || user?.branch_id}
                />
              </div>
            } />
            {/* <Route path="request-order" element={
              <BranchRequestOrder branchId={user?.branch || user?.branch_id} />
            } /> */}
            {/* <Route path="inventory-requests" element={
              <div>
                <div style={{ marginBottom: '24px' }}>
                  <Typography.Title level={2}>
                    <InboxOutlined /> Inventory Requests
                  </Typography.Title>
                  <p style={{ color: '#666', marginTop: '8px' }}>
                    Manage inventory requests and delivery statuses
                  </p>
                </div>
                <InventoryRequests branchId={user?.branch || user?.branch_id} />
              </div>
            } /> */}
            <Route path="sales" element={
              <div>
                <div style={{ marginBottom: '24px' }}>
                  <Typography.Title level={2}>
                    <BarChartOutlined /> Sales Management
                  </Typography.Title>
                </div>
                {user ? (
                  <Sales branchId={user?.branch || user?.branch_id} />
                ) : (
                  <div>Loading...</div>
                )}
              </div>
            } />
            {/* <Route path="branch-requests" element={
              <div>
                <div style={{ marginBottom: '24px' }}>
                  <Typography.Title level={2}>
                    <CheckCircleOutlined /> Branch Requests
                  </Typography.Title>
                  <p style={{ color: '#666', marginTop: '8px' }}>
                    Manage seller requests to join this branch
                  </p>
                </div>
                <BranchRequests
                  branchId={user?.branch || user?.branch_id}
                />
              </div>
            } /> */}
            <Route path="profile" element={
              <div>
                <div style={{ marginBottom: '24px' }}>
                  <Typography.Title level={2}>
                    <UserOutlined /> Profile
                  </Typography.Title>
                  <p style={{ color: '#666', marginTop: '8px' }}>
                    View your branch details and manager profile information
                  </p>
                </div>
                <BranchProfile
                  branchId={user?.branch || user?.branch_id}
                  managerId={user?.id}
                />
              </div>
            } />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  );
}


export default App;